/**
 * This is taken from
 * https://github.com/hsuanyi-chou/shadcn-ui-expansions
 * https://github.com/hsuanyi-chou/shadcn-ui-expansions/blob/main/components/ui/autosize-textarea.tsx
 *
 * We're using this rather than https://github.com/Andarist/react-textarea-autosize as that
 * library is in maintenance mode and we were hitting issues with it, specifically
 * https://github.com/Andarist/react-textarea-autosize/issues/393
 *
 * Modifications:
 * - Added an enabled prop to disable auto-sizing when not needed
 * - Cleaned up the code a bit
 */

import * as React from "react";

type UseAutosizeTextAreaProps = {
    textArea: HTMLTextAreaElement | null;
    minHeight?: number;
    maxHeight?: number;
    // The implementation dynamically sets the height of the textarea based on the scrollHeight of the element.
    // The scrollHeight includes the elments padding but not the border[1], so when we're setting the height
    // we need to account for the total height of the border in order to avoid causing the elment to always show
    // scrollbars.
    //
    // A concrete scenario of why the scrollbars would show:
    //  - scrollHeight = 34 (the height of the content + padding)
    //  - borderWidth = 2
    //  - As the height property is for the entire element (including the border) we'd consistenly
    //    setting the height to a value where the content-area + padding can't fit by 2px which causes scrollbars.
    //
    // Note (mads): We should be able to get rid of this prop in favour of reading the borderWidth of the elment
    // but that might have an performance impact, so for now I'm opting to not do it. If we find that the offsetBorder
    // prop gets in the way we can try to get rid of it.
    //
    // [1] https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
    offsetBorder?: number;
    triggerAutoSize: string;
    enabled?: boolean;
};

export const useAutosizeTextArea = ({
    textArea,
    triggerAutoSize,
    maxHeight = Number.MAX_SAFE_INTEGER,
    minHeight = 0,
    // By default our inputs have a border width of 1px
    offsetBorder = 2,
    enabled = true,
}: UseAutosizeTextAreaProps) => {
    const [init, setInit] = React.useState(true);
    React.useEffect(() => {
        if (!enabled) {
            return;
        }

        if (!textArea) {
            return;
        }

        // We only want to set the minHeight and maxHeight properties once.
        if (init) {
            textArea.style.minHeight = `${minHeight + offsetBorder}px`;
            if (maxHeight > minHeight) {
                textArea.style.maxHeight = `${maxHeight}px`;
            }
            setInit(false);
        }

        // We need to reset the height momentarily to get the correct scrollHeight for the textarea
        textArea.style.height = `${minHeight + offsetBorder}px`;
        const scrollHeight = textArea.scrollHeight;

        // We then set the height directly, outside of the render loop
        // Trying to set this with state or a ref will product an incorrect value.
        if (scrollHeight > maxHeight) {
            textArea.style.height = `${maxHeight}px`;
        } else {
            textArea.style.height = `${scrollHeight + offsetBorder}px`;
        }
    }, [textArea, triggerAutoSize, init, minHeight, maxHeight, enabled, offsetBorder]);
};
